import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  useTheme,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Box,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

const validationSchema = Yup.object({
  id_karyawan: Yup.string().required("ID Karyawan wajib diisi"),
  kode_jabatan: Yup.string().required("Jabatan wajib diisi"),
  tipe_jenjang: Yup.string().required("Tipe Jenjang wajib diisi"),
  tanggal_berlaku: Yup.date().required("Tanggal Berlaku wajib diisi"),
  file_jenjang: Yup.mixed()
    .required("File Jenjang wajib dipilih")
    .test("fileType", "Hanya mendukung PDF atau gambar", (value) => {
      return (
        value &&
        ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
      );
    }),
});

const AddJenjangModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const [filePreview, setFilePreview] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectKaryawan, setSelectKaryawan] = useState(null);

  const { list_karyawan } = useSelector((state) => state.karyawan);
  const { list_jabatan } = useSelector((state) => state.jabatan);
  const tipeOptions = [
    { value: "Kontrak 3 Bulan", text: "Kontrak 3 Bulan" },
    { value: "Kontrak 1 Tahun", text: "Kontrak 1 Tahun" },
    { value: "Tetap", text: "Tetap" },
  ];
  const formik = useFormik({
    initialValues: {
      id_karyawan: "",
      kode_jabatan: "",
      nama_jabatan: "",
      tanggal_berlaku: moment(),
      tanggal_berakhir: moment(),
      tipe_jenjang: "",
      file_jenjang: null,
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        id_karyawan: values.id_karyawan,
        kode_jabatan: values.kode_jabatan,
        cabang: values.nama_jabatan.split(" - ")[1] || "",
        tanggal_berlaku: values.tanggal_berlaku,
        tanggal_berakhir: values.tanggal_berakhir,
        tipe_jenjang: values.tipe_jenjang,
        file_jenjang: values.file_jenjang,
      };
      onSubmit(reqData);
      formik.resetForm();
      setFilePreview(null);
      onClose();
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("file_jenjang", file);
      setFilePreview(file);
      setNumPages(null); // Reset number of pages on new file
      setPageNumber(1); // Reset page number on new file
    }
  };
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(numPages || 1, prevPageNumber + 1),
    );
  };

  const renderFilePreview = () => {
    if (!filePreview) return null;

    const fileType = filePreview.type;
    if (fileType === "application/pdf") {
      return (
        <div>
          <Document
            file={filePreview}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error("PDF load error", error)}
          >
            <Page pageNumber={pageNumber} />
          </Document>
          {numPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <Button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                size="small"
                color="warning"
              >
                <ArrowBackOutlined />
              </Button>

              <Typography style={{ margin: "0 10px", lineHeight: "2" }}>
                {pageNumber} / {numPages}
              </Typography>

              <Button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                size="small"
                color="warning"
              >
                <ArrowForwardOutlined />
              </Button>
            </div>
          )}
        </div>
      );
    } else if (fileType.startsWith("image/")) {
      return (
        <img
          src={URL.createObjectURL(filePreview)}
          alt="Preview"
          style={{ maxWidth: "100%", maxHeight: "300px", marginTop: 10 }}
        />
      );
    } else {
      return (
        <Typography variant="body2">
          Preview tidak tersedia untuk tipe ini.
        </Typography>
      );
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tambah Jenjang Karyawan</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={list_karyawan}
                getOptionLabel={(option) =>
                  `${option.id_karyawan} - ${option.nama_karyawan}`
                }
                value={
                  list_karyawan.find(
                    (item) => item.id_karyawan === formik.values.id_karyawan,
                  ) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue(
                    "id_karyawan",
                    newValue?.id_karyawan || "",
                  );
                  setSelectKaryawan(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Karyawan"
                    name="id_karyawan"
                    error={
                      formik.touched.id_karyawan &&
                      Boolean(formik.errors.id_karyawan)
                    }
                    helperText={
                      formik.touched.id_karyawan && formik.errors.id_karyawan
                    }
                  />
                )}
              />
            </Grid>

            {selectKaryawan && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Jabatan saat Ini"
                    name="jabatan_lama"
                    value={selectKaryawan.nama_jabatan}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={list_jabatan}
                    getOptionLabel={(option) =>
                      `${option.kode_jabatan} - ${option.nama_jabatan}`
                    }
                    value={
                      list_jabatan.find(
                        (item) =>
                          item.kode_jabatan === formik.values.kode_jabatan,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "kode_jabatan",
                        newValue?.kode_jabatan || "",
                      );
                      formik.setFieldValue(
                        "nama_jabatan",
                        newValue?.nama_jabatan || "",
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jabatan Baru"
                        name="kode_jabatan"
                        error={
                          formik.touched.kode_jabatan &&
                          Boolean(formik.errors.kode_jabatan)
                        }
                        helperText={
                          formik.touched.kode_jabatan &&
                          formik.errors.kode_jabatan
                        }
                      />
                    )}
                  />
                </Grid>
                {/* Kolom Tanggal Berlaku */}
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Tanggal Berlaku"
                      sx={{ width: "100%" }}
                      value={formik.values.tanggal_berlaku}
                      onChange={(val) =>
                        formik.setFieldValue("tanggal_berlaku", val)
                      }
                      format="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                {/* Kolom Tanggal Berakhir + Checkbox */}
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Tanggal Berakhir"
                      sx={{ width: "100%" }}
                      value={formik.values.tanggal_berakhir}
                      onChange={(val) =>
                        formik.setFieldValue("tanggal_berakhir", val)
                      }
                      readOnly
                      disabled
                      format="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        checked={disableTanggal}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDisableTanggal(checked);
                          if (checked) {
                            formik.setFieldValue("tanggal_berakhir", null);
                          }
                        }}
                      />
                    }
                    label="Jabatan Baru"
                  /> */}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Tipe Jenjang"
                    name="tipe_jenjang"
                    value={formik.values.tipe_jenjang}
                    onChange={(val) => {
                      const selectedTipe = val.target.value;
                      const tanggalMulai = formik.values.tanggal_berlaku;
                      if (tanggalMulai && tanggalMulai.isValid()) {
                        let tanggalAkhir;
                        if (selectedTipe === "Kontrak 3 Bulan") {
                          tanggalAkhir = tanggalMulai.clone().add(3, "months");
                        } else if (selectedTipe === "Kontrak 1 Tahun") {
                          tanggalAkhir = tanggalMulai.clone().add(1, "years");
                        } else {
                          tanggalAkhir = null;
                        }
                        formik.setFieldValue("tanggal_berakhir", tanggalAkhir);
                      } else {
                        formik.setFieldValue("tanggal_berakhir", null);
                      }

                      formik.setFieldValue("tipe_jenjang", selectedTipe);
                    }}
                    error={
                      formik.touched.tipe_jenjang &&
                      Boolean(formik.errors.tipe_jenjang)
                    }
                    helperText={
                      formik.touched.tipe_jenjang && formik.errors.tipe_jenjang
                    }
                  >
                    {tipeOptions.map(({ text, value }) => (
                      <MenuItem key={value} value={value}>
                        {text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" component="label" fullWidth>
                    Pilih File Mutasi (PDF)
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  {formik.touched.file_jenjang &&
                    formik.errors.file_jenjang && (
                      <Typography color="error" variant="body2" mt={1}>
                        {formik.errors.file_jenjang}
                      </Typography>
                    )}
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              {renderFilePreview()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onClose();
              setFilePreview(null);
              formik.resetForm();
            }}
            sx={{ fontSize: "14px", fontWeight: "bold", padding: "10px 20px" }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.secondary[500],
              },
            }}
          >
            Simpan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddJenjangModal;
